import React, { useState } from 'react';
import { MessageCircle, Plus, Search, User, Clock, ChevronRight, MessageSquare, X, Send } from 'lucide-react';
import { ForumThreadView, Thread, Reply } from './ForumThread';

interface CommunityHubProps {
  threads: Thread[];
  onCreateThread: (title: string, content: string, lessonId?: number) => Promise<void>;
  onGetReplies: (threadId: string) => Promise<Reply[]>;
  onPostReply: (threadId: string, content: string) => Promise<void>;
  isLoading?: boolean;
}

export const CommunityHub = ({ threads, onCreateThread, onGetReplies, onPostReply, isLoading }: CommunityHubProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [showNewThread, setShowNewThread] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isPostingReply, setIsPostingReply] = useState(false);

  const filtered = threads.filter(t =>
    !searchQuery || t.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectThread = async (thread: Thread) => {
    setSelectedThread(thread);
    setRepliesLoading(true);
    try {
      const result = await onGetReplies(thread.id);
      setReplies(result);
    } catch {
      setReplies([]);
    } finally {
      setRepliesLoading(false);
    }
  };

  const handleCreateThread = async () => {
    if (!newTitle.trim() || !newContent.trim()) {return;}
    setIsCreating(true);
    try {
      await onCreateThread(newTitle, newContent);
      setNewTitle('');
      setNewContent('');
      setShowNewThread(false);
    } catch {
      // error handled by parent
    } finally {
      setIsCreating(false);
    }
  };

  const handlePostReply = async (threadId: string, content: string) => {
    setIsPostingReply(true);
    try {
      await onPostReply(threadId, content);
      const result = await onGetReplies(threadId);
      setReplies(result);
    } finally {
      setIsPostingReply(false);
    }
  };

  if (selectedThread) {
    return (
      <ForumThreadView
        thread={selectedThread}
        replies={replies}
        onBack={() => setSelectedThread(null)}
        onReply={handlePostReply}
        isSubmitting={isPostingReply}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Mastermind Community</h2>
          <p className="text-xs text-slate-400 font-medium">Discuss lessons, share insights, ask questions</p>
        </div>
        <button
          onClick={() => setShowNewThread(true)}
          className="flex items-center gap-2 px-5 py-3 bg-indigo-600 text-white rounded-2xl text-xs font-bold uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200"
        >
          <Plus size={16} /> New Discussion
        </button>
      </div>

      {showNewThread && (
        <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Start a Discussion</h3>
            <button onClick={() => setShowNewThread(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
              <X size={18} />
            </button>
          </div>
          <div className="space-y-4">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Discussion title..."
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
            />
            <textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="Share your thoughts, questions, or insights..."
              rows={4}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all resize-none"
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowNewThread(false)} className="px-5 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-widest hover:text-slate-700 transition-all">
                Cancel
              </button>
              <button
                onClick={handleCreateThread}
                disabled={!newTitle.trim() || !newContent.trim() || isCreating}
                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-2xl text-xs font-bold uppercase tracking-widest hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={14} />
                {isCreating ? 'Posting...' : 'Post Discussion'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search discussions..."
          className="w-full px-4 py-3 pl-10 bg-white border border-slate-200 rounded-2xl text-sm font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
        />
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-slate-400 text-xs font-medium animate-pulse">
          Loading discussions...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-xs font-medium">
          {threads.length === 0
            ? 'No discussions yet. Start the conversation!'
            : 'No discussions match your search.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((thread) => (
            <button
              key={thread.id}
              onClick={() => handleSelectThread(thread)}
              className="w-full flex items-center gap-4 p-5 bg-white border border-slate-100 rounded-3xl shadow-sm hover:border-indigo-500 hover:shadow-md transition-all text-left group"
            >
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl shrink-0 group-hover:bg-indigo-100 transition-colors">
                <MessageCircle size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-slate-800 truncate group-hover:text-indigo-700 transition-colors">
                  {thread.title}
                </h3>
                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400 font-medium">
                  <span className="flex items-center gap-1"><User size={10} /> {thread.author}</span>
                  <span className="flex items-center gap-1"><MessageSquare size={10} /> {thread.repliesCount} replies</span>
                  <span className="flex items-center gap-1"><Clock size={10} /> {new Date(thread.lastActivity).toLocaleDateString()}</span>
                </div>
              </div>
              <ChevronRight size={16} className="text-slate-300 group-hover:text-indigo-600 transition-colors shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
